import {Loader2} from "lucide-react";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate, useSearchParams} from "react-router-dom";
import {AxiosError} from "axios";

import {Button} from "@/components/ui/button";
import {cn} from "@/lib/utils";
import {PublicPageShell} from "@/src/modules/core/components";
import {useToast} from "@/src/modules/core/hooks";
import {LOGO_URL_DESKTOP} from "@/src/modules/core/utils/constants";

import {useAcknowledgeShift} from "../hooks";
import {AcknowledgeShiftResponse} from "../types";

const AcknowledgeShiftPage = () => {
  const {t} = useTranslation("shifts");
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [pendingAction, setPendingAction] = useState<"acknowledge" | "decline" | null>(null);

  const {mutateAsync: acknowledgeShift} = useAcknowledgeShift();

  const handleAction = async (action: "acknowledge" | "decline") => {
    if (!token) {
      toast.error(t("acknowledge.token_required"));
      return;
    }

    setPendingAction(action);

    try {
      const response: AcknowledgeShiftResponse = await acknowledgeShift({
        token,
        action,
      });

      if (response.success === 1) {
        toast.success(response.message);
        navigate("/login");
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      let errorMessage = t("acknowledge.failed");

      if (error instanceof AxiosError) {
        const responseData = error.response?.data as AcknowledgeShiftResponse | undefined;

        if (responseData?.message) {
          errorMessage = responseData.message;
        } else if (error.response?.data?.message) {
          errorMessage = (error.response.data as any).message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message || errorMessage;
      }

      toast.error(errorMessage);
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <PublicPageShell>
      <div className="rounded-lg bg-background p-6 shadow-lg sm:p-8">
          <div className="flex flex-col items-center mb-8">
            <div className="flex items-center justify-center h-16 w-16 rounded-full ">
              <img src={LOGO_URL_DESKTOP} alt="Image" height="100" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">STARSPRO</h1>
            <p className="text-slate-500">Security Management System</p>
          </div>

          <h2 className="text-xl font-semibold text-slate-800 mb-6 text-center">
            Shift Acknowledgment
          </h2>

          <div className="space-y-4">
            <Button
              onClick={() => handleAction("acknowledge")}
              className={cn(
                "w-full bg-green-600 hover:bg-green-700",
                pendingAction && "opacity-80 cursor-not-allowed",
              )}
              disabled={pendingAction !== null || !token}
            >
              {pendingAction === "acknowledge" ? (
                <>
                  Processing...
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                </>
              ) : (
                "Acknowledge"
              )}
            </Button>

            {/* Deny button - commented out for now */}
            {/* <Button
              onClick={() => handleAction("decline")}
              className={cn(
                "w-full bg-red-600 hover:bg-red-700",
                pendingAction && "opacity-80 cursor-not-allowed",
              )}
              disabled={pendingAction !== null || !token}
            >
              {pendingAction === "decline" ? (
                <>
                  Processing...
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                </>
              ) : (
                "Deny"
              )}
            </Button> */}
          </div>

          {!token && (
            <div className="mt-4 text-center text-sm text-red-600">
              Token is missing from URL
            </div>
          )}
        </div>

      <div className="mt-6 text-center text-xs text-muted-foreground">
        &copy; {new Date().getFullYear()} STARSPRO. All rights reserved.
      </div>
    </PublicPageShell>
  );
};

export default AcknowledgeShiftPage;
